import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { calcMean, countFreq, getPreDefinedColor } from '../../../helper';
import { fetchPortfolioDetails } from '../../../redux/actions/portfolioActions';
import LinearProgressBar from '../../utils/LinearProgressBar';
import LineChart from '../../containers/Charts/LineChart';
import PieChart from '../../containers/Charts/PieChart';
import moment from 'moment';
import {BiError} from 'react-icons/bi';
import {IoMdArrowDropdownCircle, IoMdArrowDropupCircle} from 'react-icons/io';
import { GrCircleInformation } from "react-icons/gr";
import PortfolioTable from '../../containers/PortfolioTable/PortfolioTable';
import {rate_of_return_definition, risk_definition} from '../../../constants/terms';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';

const PortfolioDetails = () => {

    // States and constants initialization
    const dispatch = useDispatch();
    const userID = useSelector(state => state.user?.user?._id);
    const user_portfolio = useSelector(state => state.portfolio.portfolio_list);
    const portfolio_changed = useSelector(state => state.portfolio.portfolio_changed);
    const portfolio_prices = useSelector(state => state.portfolio.portfolio_prices);
    const portfolio_returns = useSelector(state => state.portfolio.portfolio_returns);
    const portfolio_risks = useSelector(state => state.portfolio.portfolio_risks);
    const portfolio_return = useSelector(state => state.portfolio.portfolio_return);
    const portfolio_variance = useSelector(state => state.portfolio.portfolio_variance);

    const [isLoading, setIsLoading] = useState(false);

    // Handler for Fetching the portfolio details from the server
    const fetchPortfolioDetailsH = async () => {
            const data = {
                userID: userID,
                portfolio_changed: portfolio_changed,

            }
            // Switching to loading state
            setIsLoading(true);

            // Generating the request to the server
            await dispatch(fetchPortfolioDetails(data));
           
            setIsLoading(false);
    } 

    // UseEffect to fetch portfolio details while component mounts
    useEffect(() => {
        fetchPortfolioDetailsH();
        // eslint-disable-next-line
    }, [])

    const formatPieChartData = (type) => {
        let entities = user_portfolio?.map((stock) => stock[type]);
        let backgroundColors = [];
        if(type === "marketCap"){
            entities = entities.map((marketCap) => {
                return ( 
                    marketCap > 200000000000 
                    ? "Large Cap"
                    : marketCap > 50000000000
                    ? "Mid Cap"
                    : "Small Cap"
                )
            })
            backgroundColors=["#FDBB2F", "#7AC142", "#BB343B"];
        }
        let keys = [];
        let values = [];

        const map = countFreq(entities);

        map.forEach((value, key) => {
          keys.push(key);
          values.push(value);
        })

        const total = values.reduce((a,b) => a+b, 0);
        const distribution = values.map((value) => ((value/total)*100));

        if(type==="sector") backgroundColors = keys?.map((key, i) => getPreDefinedColor(i));

        const data = {
            labels: keys,
            datasets: [{
                label: type,
                backgroundColor: backgroundColors,
                data: distribution,
                labels: {
                    render: "true"
                },
            }],

        };
        
        const options = {
            plugins: {
              datalabels: {
                display: true,
                align: 'center',
                borderRadius: 1,
                color: "black",
                font: {
                  size: 11,
                  weight: 'bold',
                },
                formatter: (val, ctx) => {
                    return `${ctx.chart.data.labels[ctx.dataIndex].length < 10 ? ctx.chart.data.labels[ctx.dataIndex] : ctx.chart.data.labels[ctx.dataIndex]?.split(" ")[0]}\n${Math.round(val)} %`
                  },
              }
            },
          }

          return {
              data,
              options
          }
    }

    // Function to format the portfolio details in form of chart
    const formatPortfolioChartData = (allStocks) => {
        
        const consideredStocks = allStocks?.filter((stock) => stock?.ticks?.length > 200);
        
        if(consideredStocks && consideredStocks.length > 0){
            const labels = consideredStocks[0].ticks.map((tick) => moment((new Date((tick.time*1000)))).format("MMMM'YY"));
            const datasets = consideredStocks.map((stock, i) => {
                const priceData = stock.ticks.map(tick => (tick.close)/(stock.ticks[0].close));
                return {
                    data: priceData,
                    label: stock.name,
                    fill : false,
                    borderColor: getPreDefinedColor(i),
                    pointRadius: 1,
                    borderWidth: 1.5,
                }
            }) 

            const chartData = {
                labels: labels,
                datasets: datasets
            }

            const options = {
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                beginAtZero: true,
                            }
                        }
                    ],
                }
            }
            return {
                chartData: chartData,
                options: options
            }
        }

    }

    // Function to retrieve 1 Year Rate of Return of the stock
    const getStockROR = (stock_name) => {
        let ror = "Nil";
        const stock = portfolio_returns?.find((stock) => stock.name === stock_name);

        if(stock?.returns && typeof stock.returns === 'object'){
            const returnsArr = Object.values(stock.returns);
            ror = parseFloat(((calcMean(returnsArr) * 52) * 100).toFixed(2));
        }
        return ror || "NIL";
    }

    // Function to retrive 1 Year Stock Volatility of the stock
    const getStockRisk = (stock_name) => {
        let stock_risk = "NIl";
        const stock = portfolio_risks?.find((stock) => stock.name === stock_name);

        if(stock && stock?.risk){
            stock_risk = stock.risk;
        }

        return stock_risk;
    }

    // Function to format portfolio data in form of table
    const formatPortfolioTable = (data) => {
        const columns = [<span>S.No</span>, 
                         <span>Stock Name</span>,
                         <span>
                             1Y Rate-Of-Return 
                             <button type="button" className="btn btn-link" data-bs-toggle="tooltip" data-bs-placement="top" title={rate_of_return_definition}>
                                <GrCircleInformation />
                             </button>
                        </span>, 
                         <span> 
                             Risk % 
                             <button type="button" className="btn btn-link" data-bs-toggle="tooltip" data-bs-placement="top" title={risk_definition}>
                                <GrCircleInformation data-bs-toggle="tooltip" data-bs-placement="top" title=""/>
                             </button>
                         </span>, 
                         <span> Market Cap</span>, 
                         <span>Exchange</span>, 
                         <span> Sector</span>
                        ];
        const entities = data?.map((stock,i) => ([
                                                    <strong>{i+1}</strong> , 
                                                    <strong>{stock.name}</strong> , 
                                                    <strong style={{color: `${ getStockROR(stock.name) < 0? "#ff0000": "#157516"}`}}>
                                                         {
                                                            getStockROR(stock.name) < 0 ? (
                                                                <IoMdArrowDropdownCircle fill="#ff0000"/>
                                                            ) : (
                                                                <IoMdArrowDropupCircle fill="#157516"/>
                                                            )
                                                        }
                                                        <small className="mx-1">
                                                            {`${getStockROR(stock.name)}%`}
                                                        </small>
                                                    </strong>, 
                                                     <strong style={{color: "#ff0000"}}>
                                                        <small className="mx-1">
                                                            {`${getStockRisk(stock.name)}%`}
                                                        </small>
                                                    </strong>, 
                                                    stock.marketCap > 200000000000 
                                                    ? "Large Cap"
                                                    : stock.marketCap > 50000000000
                                                    ? "Mid Cap"
                                                    : "Small Cap", 
                                                    stock.exchange, 
                                                    stock.sector,
                                                ]))
        return {
            columns: columns,
            entities: entities
        }
    }

    // Error Content in case of empty portfolio
    const emptyPortfolio = (
        <div>
            <h5>Add stocks to portfolio for deeper analysis</h5>
        </div>
    )

    const chartError = (
        <div className="border border-2 rounded-lg p-3 bg-white">
            <h4>
                <BiError color="red"/>
                <span className="mx-3">Chart Could Not Be Fetched</span>
            </h4>
            <p>
                Lorem ipsum dolor sit amet, consectetur adipisicing elit. Corporis, beatae?
            </p>
        </div>
    )

    return (
            <div className="text-center my-5">
                {
                    user_portfolio?.length === 0 
                    ? emptyPortfolio
                    : isLoading
                    ? <LinearProgressBar />
                    : 
                    portfolio_prices.length > 0
                    ? 
                   (
                       <div className="mt-5">
                            {
                                formatPortfolioChartData(portfolio_prices)?.chartData &&
                                formatPortfolioChartData(portfolio_prices)?.options
                                ?
                                  <LineChart 
                                  data={formatPortfolioChartData(portfolio_prices).chartData} 
                                  options={formatPortfolioChartData(portfolio_prices).options}/>
                                : <div className="mx-auto">
                                    {chartError}
                                  </div>
                            }
                            <br/>
                            <hr/>
                            <div className="mt-5">
                                <div className="mt-5">
                                    <h4>
                                        Your Portfolio Breakdown
                                    </h4>
                                </div>
                                <div className="mt-5">
                                    <PortfolioTable 
                                        columns={formatPortfolioTable(user_portfolio).columns}
                                        entities={formatPortfolioTable(user_portfolio).entities}
                                    />
                                </div>
                            </div>
                            <br/>
                            <hr/>
                            <div className="mt-5 row justify-content-evenly">
                                <div className="col-xs-12">
                                    <h4>
                                        Your Portfolio's Risks and Returns
                                    </h4>
                                </div>
                                <div className="col-lg-5 mt-5">
                                    <div className="w-100 mx-auto">
                                        <div className="w-75">
                                            <h5 className="mb-5">Portfolio's Annual Return</h5>
                                            <CircularProgressbar
                                                value={parseFloat((portfolio_return*100).toFixed(2))}
                                                text={`${parseFloat((portfolio_return*100).toFixed(2))}%`}
                                                styles={buildStyles({
                                                        pathColor: "#0d6efd",     
                                                })}
                                            />
                                            <p className="mt-5">
                                                Portfolio return refers to the gain or loss realized by an investment portfolio containing several types of investments.Portfolios aim to deliver returns based on the stated objectives of the investment strategy, as well as the risk tolerance.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-5 mt-5">
                                    <div className="w-100">
                                       <div className="w-75">
                                        <h5 className="mb-5">Portfolio's Annual Risk</h5>
                                        <CircularProgressbar
                                                value={parseFloat((portfolio_variance*100).toFixed(2))}
                                                text={`${parseFloat((portfolio_variance*100).toFixed(2))}%`}
                                                styles={buildStyles({
                                                        pathColor: "#dc3545",
                                                })}
                                            />
                                            <p className="mt-5">
                                                Portfolio risk is a chance that the combination of assets or units, within the investments that you own, fail to meet financial objectives. Each investment within a portfolio carries its own risk, with higher potential return typically meaning higher risk.
                                            </p>
                                       </div>
                                    </div>        
                                </div>
                            </div>
                            <br/>
                            <hr/>
                            <div className="mt-5 row justify-content-evenly">
                                <div className="col-xs-12">
                                    <h4>
                                        Your Portfolio's Industry Breakdown 
                                    </h4>
                                </div>
                                <div className="col-lg-5 mt-5">
                                    <h5>Sector Distribution</h5>
                                    <div className="w-100 mt-5">
                                        {
                                            formatPieChartData("sector")?.data &&
                                            formatPieChartData("sector")?.options
                                            ?   <PieChart data={formatPieChartData("sector").data} 
                                                          options={formatPieChartData("sector").options}/>
                                            : <></>
                                        }
                                    </div>
                                </div>
                                <div className="col-lg-5 mt-5">
                                    <h5>Market Distribution</h5>
                                    <div className="w-100 mt-5">
                                        {
                                            formatPieChartData("marketCap")?.data &&
                                            formatPieChartData("marketCap")?.options
                                            ? <PieChart data={formatPieChartData("marketCap").data}
                                                        options={formatPieChartData("marketCap").options} /> 
                                            : <></>
                                        }
                                    </div>        
                                </div>
                            </div>
                        </div>
                   ) :
                   <></>
                }
                 
            </div>
    )
}

export default PortfolioDetails
